import React, { useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";

import useSecurityObject from "../../hooks/useSecurityObject";
import { normalizePath } from "../../security/SecurityObjectProvider";

import { httpClient } from "../../http";
import { userStorage } from "../../utils/storage";

type Props = {
    children: React.ReactElement;

    objectCode?: string;

    requireAuth?: boolean;

    redirectToIfNotAuth?: string;
    redirectToIfNoPermission?: string;
};

const RouteGuardLoader = () => (
    <Box
        sx={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
        <CircularProgress />
    </Box>
);

const logUnauthorizedAttempt = (reason: string, objectCode: string | undefined, path: string) => {
    // eslint-disable-next-line no-console
    console.warn("[Security] Intento no autorizado", { reason, objectCode, path });
};

const isAuthenticatedNow = () => {
    try {
        const accessToken = httpClient.getAccessToken();
        if (!accessToken) return false;

        try {
            const u = (userStorage as any).get?.();
            return !!u?.userId || (userStorage as any).isLogged?.() === true;
        } catch {
            (userStorage as any).removeFromStorage?.();
            return false;
        }
    } catch {
        return false;
    }
};

// Soporta:
// - "/checkout-reanudar/:token"
// - "/algo/*" (wildcard al final, matchea el resto)
const matchRoutePattern = (patternRaw: string, pathRaw: string) => {
    const pattern = normalizePath(patternRaw);
    const path = normalizePath(pathRaw);

    // Exact fast-path
    if (pattern === path) return true;

    const pSegs = pattern.split("/").filter(Boolean);
    const aSegs = path.split("/").filter(Boolean);

    let i = 0;
    let j = 0;

    while (i < pSegs.length && j < aSegs.length) {
        const p = pSegs[i];
        const a = aSegs[j];

        if (p === "*") return true; // wildcard consume el resto
        if (p.startsWith(":")) {
            // param consume 1 segmento
            i++;
            j++;
            continue;
        }

        if (p !== a) return false;
        i++;
        j++;
    }

    if (i === pSegs.length && j === aSegs.length) return true;

    if (i === pSegs.length - 1 && pSegs[i] === "*") return true;

    return false;
};

export function SafetyRouteMiddleware({
    children,
    objectCode,
    requireAuth = false,
    redirectToIfNotAuth = "/login",
    redirectToIfNoPermission = "/",
}: Props) {
    const location = useLocation();
    const currentPath = normalizePath(location.pathname);

    const { hasReadPermission, isLoadingPermissions, getByCode } = useSecurityObject();

    const isAuthenticated = useMemo(() => isAuthenticatedNow(), [location.key]);
    const shouldWaitPermissions = Boolean(objectCode);

    if (shouldWaitPermissions && isLoadingPermissions) {
        return <RouteGuardLoader />;
    }

    // Auth gate
    if (requireAuth && !isAuthenticated) {
        logUnauthorizedAttempt("not_authenticated", objectCode, currentPath);
        return <Navigate to={redirectToIfNotAuth} state={{ from: location }} replace />;
    }

    // Permission gate (opcional)
    if (objectCode) {
        if (!hasReadPermission(objectCode)) {
            logUnauthorizedAttempt("no_permission", objectCode, currentPath);
            return <Navigate to={redirectToIfNoPermission} state={{ from: location }} replace />;
        }
    }

    return children;
}
